import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { api, User } from "@/lib/api";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      if (!api.isAuthenticated()) {
        setIsLoading(false);
        return;
      }

      try {
        const currentUser = await api.getCurrentUser();
        setUser(currentUser);

        // If user has an API key, store it
        if (currentUser.apiKey) {
          api.setApiKey(currentUser.apiKey);
          logger.log("API key set from user profile:", currentUser.apiKey);
        } else {
          console.warn("User authenticated but no API key found in profile");
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        toast.error('Authentication error. Please log in again.');
        // Clear authentication on error
        api.logout();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const { user, token } = await api.login(email, password);
      
      // Store the user and the API key
      setUser(user);
      if (user.apiKey) {
        api.setApiKey(user.apiKey);
        logger.log("API key set during login:", user.apiKey);
      } else {
        console.warn("Logged in but no API key found in user object");
      }
      
      toast.success(`Welcome back, ${user.username}!`);
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Login failed. Please check your credentials.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    api.logout();
    setUser(null);
    toast.info('You have been logged out.');
  };

  return (
    <AuthContext.Provider 
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 